import { ApiBuiltStatus, ApiElementProgress } from "avvir";

import DateConverter from "../../services/converters/date_converter";
import Deviation, { DeviationArgs } from "./deviation";
import { DateLike, ModifyPartial } from "type_aliases";
import { DEVIATED, IN_PLACE, NOT_BUILT } from "./enums/scan_label";
import { UniformatId } from "uniformat";
import { stripUndefinedsFromObject } from "../../services/utilities/general/strip_undefineds_from_object";
import addDateGetterAndSetterToDomainModel, { toDomainDate } from "../../services/utilities/mixins/add_date_getter_and_setter_to_domain_model";
import addReadOnlyPropertiesToModel from "../../services/utilities/mixins/add_read_only_properties_to_model";
import { isBigBimViewerPerformanceOn } from "../../services/utilities/big_bim_viewer_performance";

export type PlannedBuildingElementArg = ModifyPartial<PlannedBuildingElement, {
  builtAt: DateLike
  fixedAt: DateLike
  deviation: DeviationArgs
  fixedDeviation: DeviationArgs
  fixedAtDeviation: DeviationArgs
  progressHistory?: ApiElementProgress[]
}>

export default class PlannedBuildingElement {
  constructor(args: PlannedBuildingElementArg = {}) {
    if (isBigBimViewerPerformanceOn()) {
      // Plain fields rather than the defineProperty mixins: a floor can hold over a million elements and the
      // per-instance accessors triple each one's memory. Assign dates with toDomainDate().
      this.id = args.id;
      this.builtAt = toDomainDate(args.builtAt);
      this.fixedAt = toDomainDate(args.fixedAt);
    } else {
      addReadOnlyPropertiesToModel(this, { id: args.id });
      addDateGetterAndSetterToDomainModel(this, "builtAt", args.builtAt);
      addDateGetterAndSetterToDomainModel(this, "fixedAt", args.fixedAt);
    }
    this.globalId = args.globalId;
    this.name = args.name;
    this.ifcType = args.ifcType;
    this.uniformat = args.uniformat;
    this.masterformat = args.masterformat;
    this.revitCategory = args.revitCategory;
    this.revitFamily = args.revitFamily;
    this.revitType = args.revitType;
    this.itemId = args.itemId;
    this.discipline = args.discipline;
    this.primaryUnitOfMeasurement = args.primaryUnitOfMeasurement;
    this.primaryMeasurement = args.primaryMeasurement;
    this.navisworksGuid = args.navisworksGuid;
    this.issueId = args.issueId;
    this.excludeFromAnalysis = args.excludeFromAnalysis;
    this.floorSpan = args.floorSpan;
    this.subcontractorId = args.subcontractorId;
    this.unitCost = args.unitCost;
    this.loaded = args.loaded;
    if (args.deviation) {
      this.deviation = { ...new Deviation(args.deviation) };
    }
    if (args.fixedDeviation) {
      this.fixedDeviation = { ...new Deviation(args.fixedDeviation) };
    }
    if (args.isNew != null) {
      this.isNew = args.isNew;
    }

    this.verified = typeof args.verified === "undefined" ? false : args.verified;
    this.reviztoId = args.reviztoId;
    this.reviztoIfcGuid = args.reviztoIfcGuid;
    this.exportedToBcf = args.exportedToBcf;
    this.partialProgressPercent = args.partialProgressPercent;
    this.trade = args.trade;
    this.builtStatus = args.builtStatus;
    this.progressHistory = args.progressHistory;
  }

  readonly id: number;
  name?: string;
  globalId: string;
  itemId?: string | null;
  ifcType?: string;
  discipline?: string;
  uniformat?: UniformatId;
  masterformat?: string;
  revitFamily?: string;
  revitType?: string;
  revitCategory?: string;
  primaryUnitOfMeasurement?: string;
  primaryMeasurement?: number;
  navisworksGuid?: string;
  issueId?: number;
  excludeFromAnalysis?: boolean;
  floorSpan?: number;
  subcontractorId?: number;
  unitCost?: number;
  isNew?: boolean;
  loaded?: boolean;
  /** In most cases, do not use this directly - use isBuilt() */
  builtAt: Date;
  /** In most cases, do not use this directly - use getDeviation() */
  deviation: Deviation;
  /** In most cases, do not use this directly - use isFixed() */
  fixedAt: Date;
  /** In most cases, do not use this directly - use getDeviation() */
  fixedDeviation: Deviation;
  verified?: boolean;
  reviztoId?: string;
  reviztoIfcGuid?: string;
  exportedToBcf?: boolean;
  partialProgressPercent?: number;
  trade?: string;
  /**
   * Built status as computed by the gateway, present only on elements that arrived via the minimal
   * payload. It lets the model be colored before builtAt/deviation exist.
   */
  builtStatus?: ApiBuiltStatus;
  progressHistory?: ApiElementProgress[];

  /** The element with details loaded for it (eg: on selection) laid over the top. */
  static withDetails(element: PlannedBuildingElement, details?: PlannedBuildingElementArg): PlannedBuildingElement {
    if (!details) {
      return element;
    }
    return new PlannedBuildingElement({ ...element, ...stripUndefinedsFromObject(details) as PlannedBuildingElementArg, globalId: element.globalId });
  }

  static updatePartialProgress(element: PlannedBuildingElement, partialProgressPercent: number, scanDatasetId: number) {
    element.partialProgressPercent = partialProgressPercent;
    if (element.progressHistory != null) {
      for (let progress of Object.values(element.progressHistory)) {
        if (progress.scanDatasetId === scanDatasetId) {
          progress.progress = partialProgressPercent;
          return;
        }
      }
    }
  }

  static getPartialProgress(element: PlannedBuildingElement, scanDate: Date, scanDatasetId: number, historyEnabledFlag: boolean = false): number {
    const isBuilt = PlannedBuildingElement.isBuilt(element, scanDate);
    if (!isBuilt) {
      return 0;
    }

    if (historyEnabledFlag && element.progressHistory != null) {
      for (let progress of Object.values(element.progressHistory)) {
        if (progress.scanDatasetId === scanDatasetId) {
          return progress.progress;
        }
      }
    }

    return element.partialProgressPercent;
  }

  static hasPartialProgress = (element: PlannedBuildingElement) => {
    return element.partialProgressPercent != null
           && element.partialProgressPercent > 0
           && element.partialProgressPercent < 1;
  };

  static isBuilt = (element: PlannedBuildingElement, scanDate: Date) => {
    return element?.builtAt != null && DateConverter.isOnOrAfterDay(element.builtAt, scanDate);
  };

  static isPartiallyBuilt = (element: PlannedBuildingElement, scanDate: Date) => {
    return PlannedBuildingElement.isBuilt(element, scanDate) && PlannedBuildingElement.hasPartialProgress(element);
  };

  static isFixed = (element: PlannedBuildingElement, scanDate: Date) => {
    return element?.fixedAt != null && DateConverter.isOnOrAfterDay(element.fixedAt, scanDate);
  };

  static isDeviated = (element: PlannedBuildingElement, scanDate: Date) => {
    return !!PlannedBuildingElement.getDeviation(element, scanDate);
  };

  static getScanLabel = (element: PlannedBuildingElement, scanDate: Date) => {
    // elements from the minimal payload have no builtAt or deviation to compute from, so
    // fall back on the status the gateway computed. ApiBuiltStatus values are exactly the
    // IN_PLACE/NOT_BUILT/DEVIATED scan labels. Every full-data load clears builtStatus, so the
    // scan-date-aware path below takes over as soon as the real data arrives.
    if (element?.builtStatus != null) {
      return element.builtStatus;
    }
    if (PlannedBuildingElement.isDeviated(element, scanDate)) {
      return DEVIATED;
    } else if (PlannedBuildingElement.isBuilt(element, scanDate)) {
      return IN_PLACE;
    } else {
      return NOT_BUILT;
    }
  };

  static getDeviation = (element: PlannedBuildingElement, scanDate: Date): Deviation => {
    if (PlannedBuildingElement.isFixed(element, scanDate)) {
      if (Deviation.isEmpty(element?.fixedDeviation)) {
        return null;
      } else {
        return element?.fixedDeviation;
      }
    } else if (PlannedBuildingElement.isBuilt(element, scanDate)) {
      if (Deviation.isEmpty(element?.deviation)) {
        return null;
      } else {
        return element?.deviation;
      }
    } else {
      return null;
    }
  };
}
